// BotoxRecall.jsx — live front-desk recall queue for clients due/overdue for
// Botox (last neuromodulator 80+ days ago, no future appointment).
// Newest-due first. Front desk works each client to an outcome:
//   To Call → No Answer (call back) → Booked. Categories + counts let the desk
//   see who still needs calling, who to retry, and how many they've booked.
import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const C = {
  card:"#ffffff", border:"#e8e4df", borderGold:"rgba(180,140,60,0.3)",
  goldBg:"rgba(180,140,60,0.08)", gold:"#b48c3c",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)", red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  orange:"#cc6600", orangeL:"rgba(204,102,0,0.10)", blue:"#2563eb",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";
const statusOf = (r) => r?.status || "todo";

export default function BotoxRecall() {
  const [due, setDue] = useState([]);
  const [recall, setRecall] = useState({});   // ALL recall rows, keyed by client_id
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState("todo"); // todo | no_answer | booked | all

  useEffect(() => {
    load();
    const ch = supabase.channel("botox-recall-" + Math.random())
      .on("postgres_changes", { event: "*", schema: "public", table: "botox_recall" }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "appointments" }, load)
      .subscribe();
    // Keep the list fresh even if left open all day: clients age past 80 days by
    // the passage of time (no DB event fires for that), so re-query periodically.
    const iv = setInterval(load, 15 * 60 * 1000);
    return () => { supabase.removeChannel(ch); clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    // Newest-due first (fewest days since last visit at the top).
    const { data: d } = await supabase.from("botox_due").select("*").order("days_since", { ascending: true });
    setDue(d || []);
    const { data: r } = await supabase.from("botox_recall").select("*");
    const map = {}; (r || []).forEach(x => { map[x.client_id] = x; });
    setRecall(map);
    setLoading(false);
  };

  const setStatus = async (row, status) => {
    const cid = row.client_id;
    setBusy(cid);
    const { data: { user } } = await supabase.auth.getUser();
    const { data: prof } = user ? await supabase.from("profiles").select("full_name,email").eq("id", user.id).maybeSingle() : { data: null };
    const by = prof?.full_name || prof?.email || "Front desk";
    const now = new Date().toISOString();
    const existing = recall[cid];
    const patch = {
      client_id: cid, status, name: row.name || existing?.name, phone: row.phone || existing?.phone,
      last_called_at: now, last_called_by: by, updated_at: now,
    };
    if (status === "booked") patch.booked_at = now;

    if (status === "no_answer") {
      patch.call_attempts = (existing?.call_attempts || 0) + 1;
      // Auto-text the client (as Sophia) — she's due + $9.99 special — so she
      // can reply and Sophia (AI receptionist) continues the thread and books.
      // Guard against double-texting on rapid re-clicks: skip if we already
      // texted her in the last 12h (keeps a genuine follow-up on a later day).
      const lastSms = existing?.sms_sent_at ? new Date(existing.sms_sent_at).getTime() : 0;
      const recentlyTexted = lastSms && (Date.now() - lastSms) < 12 * 3600 * 1000;
      if (row.phone && !recentlyTexted) {
        const first = (row.name || "there").trim().split(/\s+/)[0];
        const msg = `Hi ${first}, it's Sophia from Nova Med Spa 💛 You're due for your Botox refresh! I'd love to get you back in — and we have a $9.99/unit special going on right now. What day & time works best for you? (Reply STOP to opt out)`;
        try {
          const { data: res, error } = await supabase.functions.invoke("send-sms", {
            body: { to: row.phone, message: msg, sender: "ai", source: "botox_recall" },
          });
          if (error || res?.ok === false) {
            patch.sms_error = res?.skipped === "dnc" ? "Client opted out (no text sent)" : String(error?.message || res?.error || "Text failed to send");
          } else {
            patch.sms_sent_at = now; patch.sms_error = null;
          }
        } catch (e) {
          patch.sms_error = String(e?.message || e);
        }
      }
    }

    await supabase.from("botox_recall").upsert(patch, { onConflict: "client_id" });
    setBusy(null); load();
  };

  const reset = async (cid) => {
    await supabase.from("botox_recall").upsert({ client_id: cid, status: null, updated_at: new Date().toISOString() }, { onConflict: "client_id" });
    load();
  };

  const cTodo = due.filter(d => statusOf(recall[d.client_id]) === "todo").length;
  const cNoAns = due.filter(d => statusOf(recall[d.client_id]) === "no_answer").length;
  const bookedRows = Object.values(recall).filter(r => r.status === "booked");
  const cBooked = bookedRows.length;

  const tabs = [
    { id: "todo", label: "📋 To Call", n: cTodo, color: C.gold },
    { id: "no_answer", label: "📵 No Answer", n: cNoAns, color: C.orange },
    { id: "booked", label: "✅ Booked", n: cBooked, color: C.green },
    { id: "all", label: "All Due", n: due.length, color: C.muted },
  ];

  // Rows to render for the active tab.
  let rows;
  if (filter === "booked") {
    rows = bookedRows.map(r => ({ ...r, _booked: true }));  // snapshot rows (may have left the view)
  } else if (filter === "all") {
    rows = due;
  } else {
    rows = due.filter(d => statusOf(recall[d.client_id]) === filter);
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: C.text, fontFamily: S }}>💉 Botox Recall</div>
        <div style={{ fontSize: 12, color: C.muted }}>Clients due (80+ days since last Botox) with no upcoming appointment — newest due first. Call to rebook; mark each outcome so nobody falls through.</div>
        <div style={{ fontSize: 11.5, color: C.orange, marginTop: 4, fontWeight: 600 }}>📵 Tapping “No Answer” auto-texts the client (as Sophia) about her $9.99 Botox special — when she replies, Sophia continues the chat and books her.</div>
      </div>

      {/* Category tabs / counts */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {tabs.map(t => {
          const active = filter === t.id;
          return (
            <button key={t.id} onClick={() => setFilter(t.id)} style={{
              display: "flex", alignItems: "center", gap: 7, padding: "9px 15px", borderRadius: 9, cursor: "pointer",
              fontFamily: F, fontSize: 12.5, fontWeight: 700, transition: "all .15s",
              background: active ? t.color : C.card, color: active ? "#fff" : C.text,
              border: "1px solid " + (active ? t.color : C.border),
            }}>
              {t.label}
              <span style={{ fontSize: 11, fontWeight: 800, padding: "1px 8px", borderRadius: 50, background: active ? "rgba(255,255,255,0.25)" : C.light, color: active ? "#fff" : t.color }}>{t.n}</span>
            </button>
          );
        })}
        <button onClick={load} style={{ marginLeft: "auto", padding: "9px 14px", background: C.goldBg, color: C.gold, border: "1px solid " + C.borderGold, borderRadius: 9, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: F }}>↻ Refresh</button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: C.muted }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", background: C.light, border: "1px dashed " + C.border, borderRadius: 10 }}>
          <div style={{ fontSize: 34, marginBottom: 8 }}>{filter === "booked" ? "📅" : "🎉"}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
            {filter === "booked" ? "No booked recalls yet — mark clients as Booked when they rebook." : filter === "no_answer" ? "No pending call-backs." : "All caught up — nobody left to call."}
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: 12 }}>
          {rows.map(r => {
            const rc = recall[r.client_id];
            const st = statusOf(rc);
            const isOverdue = r.days_since >= 90;
            return (
              <div key={r.client_id} style={{
                background: C.card, border: "1px solid " + C.border,
                borderLeft: `4px solid ${st === "booked" ? C.green : st === "no_answer" ? C.orange : isOverdue ? C.red : C.gold}`,
                borderRadius: 11, padding: 14,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{r.name || "Unknown"}</div>
                  {r._booked ? (
                    <span style={{ fontSize: 10, fontWeight: 800, padding: "3px 9px", borderRadius: 50, background: C.greenL, color: C.green }}>✅ BOOKED</span>
                  ) : (
                    <span style={{ fontSize: 10, fontWeight: 800, padding: "3px 9px", borderRadius: 50, whiteSpace: "nowrap", background: isOverdue ? C.redL : C.orangeL, color: isOverdue ? C.red : C.orange }}>
                      {isOverdue ? "🔴 OVERDUE" : "🟡 DUE"} · {r.days_since}d
                    </span>
                  )}
                </div>

                <div style={{ fontSize: 12, color: C.muted, marginTop: 6, lineHeight: 1.6 }}>
                  📞 {r.phone ? <a href={"tel:" + r.phone} style={{ color: C.blue, fontWeight: 700, textDecoration: "none" }}>{r.phone}</a> : <span style={{ color: C.dim }}>no phone</span>}<br />
                  {r.email && <>✉️ {r.email}<br /></>}
                  {r.last_visit && <>🗓️ Last visit: <strong>{r.last_visit}</strong> ({r.days_since} days ago)<br /></>}
                  {r.last_treat && <>💉 {r.last_treat}</>}
                </div>

                {/* Outcome line */}
                {st === "booked" && rc?.booked_at && (
                  <div style={{ fontSize: 10.5, color: C.green, marginTop: 6, fontWeight: 700 }}>✅ Booked {new Date(rc.booked_at).toLocaleDateString()}{rc.last_called_by ? ` by ${rc.last_called_by}` : ""}</div>
                )}
                {st === "no_answer" && (
                  <div style={{ fontSize: 10.5, color: C.orange, marginTop: 6, fontWeight: 700 }}>📵 No answer · {rc?.call_attempts || 1} attempt{(rc?.call_attempts || 1) > 1 ? "s" : ""}{rc?.last_called_at ? ` · last ${new Date(rc.last_called_at).toLocaleDateString()}` : ""}</div>
                )}
                {rc?.sms_sent_at && (
                  <div style={{ fontSize: 10, color: C.green, marginTop: 3, fontWeight: 600 }}>📤 Auto-text sent {new Date(rc.sms_sent_at).toLocaleDateString()} — Sophia will handle the reply</div>
                )}
                {rc?.sms_error && (
                  <div style={{ fontSize: 10, color: C.red, marginTop: 3, fontWeight: 600 }}>⚠️ {rc.sms_error}</div>
                )}

                {/* Actions */}
                <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                  <button onClick={() => setStatus(r, "booked")} disabled={busy === r.client_id} style={btn(`linear-gradient(135deg,${C.green},#1e6b3f)`, "#fff", 1)}>
                    {busy === r.client_id ? "…" : "✅ Booked"}
                  </button>
                  <button onClick={() => setStatus(r, "no_answer")} disabled={busy === r.client_id} style={btn("#fff", C.orange, 1, C.orange)}>📵 No Answer</button>
                  {st !== "todo" && <button onClick={() => reset(r.client_id)} title="Move back to To Call" style={btn("#fff", C.muted, 0, C.border)}>↩</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function btn(bg, color, flex, borderColor) {
  return {
    flex: flex ? 1 : "0 0 auto", padding: "8px 12px", background: bg, color,
    border: borderColor ? "1px solid " + borderColor : "none",
    borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: F,
  };
}
